import { useQuery } from "react-query";
import { sureApiCall } from "src/api/axiosClient/sureApi";
import { zodParseWithLog } from "src/api/apiErrorLogs";
import { ActionsListSchema, ActionsListType, SureCsrTheme } from "./useGetOneAction";
import { isQueryReady } from "src/api/axiosClient/utils";
import { ACTION_IMAGE_REFRESH_QUERY_OPTIONS } from "src/api/actionImageRefresh";

export type CatalogByCsr = {
    parent: {
        id: number;
        title: string;
    };
    items: FlattenedActionsCsrThemes[];
};
export type CatalogListByCsr = CatalogByCsr[];

export const getAllActionsKey = "getAllActions" as const;

export type Options = {
    ssqVersionId: number | undefined;
};
export const useGetAllActions = (options: Options) => {
    const { ssqVersionId } = options;

    const isEnabled = ssqVersionId !== undefined;

    const query = useQuery({
        queryKey: [getAllActionsKey, ssqVersionId],
        cacheTime: 5,
        enabled: isEnabled,
        ...ACTION_IMAGE_REFRESH_QUERY_OPTIONS,
        queryFn: async () => {
            const url = `/api/actions`;
            const response = await sureApiCall.get(url, {
                params: {
                    itemsPerPage: 20000,
                    ssqVersionId,
                    "order[position]": "asc",
                },
            });
            const actions = response.data["hydra:member"];
            const data = zodParseWithLog(actions, ActionsListSchema.array(), getAllActionsKey);
            return data;
        },
        onError: () => {
            return [];
            // return some fallback data
        },
    });

    const actions = query.data;

    const isReady = isEnabled && isQueryReady(query, isEnabled);

    const getActionsByCsrTheme = (): ActionsByCsrThemes => {
        if (actions === undefined) return {};

        // This makes sure items are duplicated by CsrThemes.
        // They are supposed to be present in only one, BUT, in case they appear in MULTIPLE
        // We want to show them in EVERY CsrThemes.
        const flattenActionsByCsrThemes = actions.flatMap((action) => {
            const newActionList = action.csrThemes.reduce<FlattenedActionsCsrThemes[]>((acc, csrTheme) => {
                acc.push({
                    ...action,
                    csrTheme,
                });
                return acc;
            }, []);
            return newActionList;
        });

        // Group by CSR theme - actions are already sorted by position from the API
        const groupedActions = Object.groupBy(flattenActionsByCsrThemes, (action) => action.csrTheme.title);

        return groupedActions as ActionsByCsrThemes;
    };

    return {
        actions,
        isReady,
        getActionsByCsrTheme,
        ...query,
    };
};

export type FlattenedActionsCsrThemes = Omit<ActionsListType, "csrThemes"> & {
    csrTheme: SureCsrTheme;
};
export type ActionsByCsrThemes = Partial<Record<string, FlattenedActionsCsrThemes[]>>;
